import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { processGoogleCallback } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      // Check for authorization code from Google OAuth (in query params)
      const code = searchParams.get('code');
      
      if (code) {
        // New Google OAuth flow
        try {
          await processGoogleCallback(code);
          toast.success('Welcome to Prysh AI!');
          navigate('/dashboard', { replace: true });
        } catch (error) {
          console.error('Google auth error:', error);
          toast.error(error.message || 'Authentication failed');
          navigate('/login');
        }
        return;
      }

      // Check for error from Google
      const error = searchParams.get('error');
      if (error) {
        toast.error('Google sign-in was cancelled');
        navigate('/login');
        return;
      }

      // No valid params
      toast.error('Invalid authentication response');
      navigate('/login');
    };

    processAuth();
  }, [searchParams, navigate, processGoogleCallback]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center" data-testid="auth-callback">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
